import React, { useCallback, useEffect } from 'react'
import { useReactiveVar } from '@apollo/client'
import { WebSocketLink } from '@apollo/client/link/ws'
import { T } from '@transifex/react'
import { SubscriptionClient } from 'subscriptions-transport-ws'

import { getOverrideApiUrl } from 'src/new-components/forms/app-config/state'
import { userVar } from 'src/reactive-variables'
import { message } from 'src/ui/toast'

import { getJWTToken } from './apollo'
import { useRefEnhanced } from '../../hooks/useRefEnhanced'

const { host, protocol } = window.location
const secure = protocol.includes('s') ? 'wss' : 'ws'

export const wsUri = `${
  process.env.REACT_APP_API_SSL === 'true' ? 'wss' : secure
}://${getOverrideApiUrl() || process.env.REACT_APP_API_URL || host}/ws`

const wsClient = new SubscriptionClient(`${wsUri}/graphql/`, {
  reconnect: true,
  connectionParams: () => {
    const jwtToken = getJWTToken()
    return jwtToken
      ? {
          auth_token: jwtToken,
        }
      : {}
  },
  timeout: 60 * 1000, //  1min
})

const DISCONNECTED_MESSAGE_KEY = 'DISCONNECTED_MESSAGE_KEY'

/**
 * True while `reconnectWs()` is closing the socket ON PURPOSE. The client
 * emits `disconnected` synchronously from that close, and the app-wide red
 * "Disconnected" toast below listens to it — so every Reconnect click used
 * to flash the toast until the ack (review on #1260). The toast stays quiet
 * for exactly that one emit; a reconnect that then fails to come back
 * emits its own, undeliberate `disconnected` from the library's retry path,
 * and that one shows the toast as before. The status listeners
 * (`onWsStatusChange`: the live feed's mode machine, the record-lease
 * adapter) still see the deliberate disconnect — the feed's reseed on the
 * following ack IS the recovery.
 */
let closingDeliberately = false
export const isDeliberateDisconnect = (): boolean => closingDeliberately

export const WsDisconnectNotification = () => {
  const isLoggedInListener = useReactiveVar<$TSFixMe>(userVar)?.id

  const [getIsLoggedIn, setIsLoggedIn] = useRefEnhanced(isLoggedInListener)

  const removeNotification = useCallback(() => {
    message.destroy(DISCONNECTED_MESSAGE_KEY)
  }, [])

  const showNotification = useCallback(() => {
    message.error({
      key: DISCONNECTED_MESSAGE_KEY,
      content: <T _str="Disconnected" />,
      duration: 0,
    })
  }, [])

  useEffect(() => {
    setIsLoggedIn(isLoggedInListener)
    if (!isLoggedInListener) {
      removeNotification()
    }
  }, [isLoggedInListener, removeNotification, setIsLoggedIn])

  useEffect(() => {
    wsClient.onReconnected(() => {
      // need to close the message and set bool to false.
      removeNotification()
    })
    wsClient.onDisconnected(() => {
      if (isDeliberateDisconnect()) {
        return
      }
      if (getIsLoggedIn()) {
        showNotification()
      }
    })
  }, [getIsLoggedIn, removeNotification, showNotification])

  return <></>
}

export const wsLink = new WebSocketLink(wsClient)

/**
 * WebSocket connection state, exposed for the Live Operations push feed's
 * push→poll fallback (live-ops W4, SPEC D3). Purely additive: the client
 * itself stays module-private.
 */
export type WsStatus = 'connected' | 'disconnected'

/**
 * Subscribe to socket connect/reconnect/disconnect transitions. Returns an
 * unsubscribe function. `connected` fires on both the first connect and
 * every reconnect; `disconnected` when the socket drops.
 */
export const onWsStatusChange = (cb: (status: WsStatus) => void) => {
  const offConnected = wsClient.onConnected(() => cb('connected'))
  const offReconnected = wsClient.onReconnected(() => cb('connected'))
  const offDisconnected = wsClient.onDisconnected(() => cb('disconnected'))
  return () => {
    offConnected()
    offReconnected()
    offDisconnected()
  }
}

/** Current socket state (WebSocket.OPEN === 1), for seeding the live-feed
 * mode on mount instead of assuming connected. */
export const getWsStatus = (): WsStatus =>
  wsClient.status === 1 ? 'connected' : 'disconnected'

/**
 * Force a FRESH socket — the Live Operations "Reconnect" button (PRO-888
 * item 5, R8). Until this existed the button only refetched the master query
 * and never touched the socket, so a dead-but-open WebSocket stayed dead.
 *
 * Mechanism, read from the client source: `close(isForced=false,
 * closedByUser=false)` closes the raw WebSocket WITHOUT `unsubscribeAll()` or a
 * `GQL_CONNECTION_TERMINATE` (a forced close would kill the feed subscription
 * for good), emits `disconnected`, and — because it was not closed "by the
 * user" — runs the client's own `tryReconnect()`, which queues a `start` for
 * every active operation (the `onLiveOperationsFeed` subscription) and
 * reopens; the ack then emits `reconnected` → `onWsStatusChange('connected')`
 * → the live feed reseeds its positions. The client's retry state is reset
 * first, so a click works from ANY socket state:
 *   - `tryReconnect()` queues those `start`s only while `reconnecting` is
 *     false, and a socket that opened (flushing its `start`s) but is still
 *     waiting for the ack has it true — closing THAT one (a double click, or a
 *     backend that accepts the socket but never acks) would leave the
 *     replacement with no subscription while the board reads "connected". So
 *     the queue's `start`s are dropped and `reconnecting` reset before the
 *     retry (the patched `tryReconnect` below, on EVERY attempt): the
 *     replacement's `start`s are always rebuilt from the active operations,
 *     exactly once;
 *   - both backoffs are reset — the retry delay (100 ms doubling to 10 s) and
 *     the max-connect timeout (1 s growing to 60 s; normally reset only by an
 *     ack) — so the new attempt goes out within ~150 ms;
 *   - ANY socket the client holds is closed: an attempt still CONNECTING, or
 *     a dead CLOSED one — a retry refused right after a max-connect timeout,
 *     whose close the client ignores (that timeout set `closedByUser`), so it
 *     holds the socket until the NEXT timeout. The replacement's `connect()`
 *     clears the old max-connect timer, so that timer cannot kill it;
 *   - with no socket at all (`client === null`, a retry pending) `close()`
 *     is a no-op, so `tryReconnect()` is called directly.
 * All of this reaches PRIVATE members of the deprecated
 * `subscriptions-transport-ws` client — `client`, `reconnecting`,
 * `unsentMessagesQueue`, `backoff`, `maxConnectTimeGenerator`,
 * `tryReconnect` — which is why package.json pins it EXACT at 0.9.19 (no
 * caret): the backend speaks only its legacy protocol (PRO-137), so the
 * client cannot be swapped, and a bump that renamed one of those would make
 * this a silent no-op. THE UPGRADE GATE IS `__tests__/ws-reconnect.test.ts`,
 * which drives the real client over a fake WebSocket: a bump that keeps it
 * green keeps the button honest. The guard below checks `tryReconnect` and
 * WARNS when it bails, so a drifted client is never a quiet fallback.
 */
type ReconnectInternals = {
  client: unknown | null
  reconnecting?: boolean
  unsentMessagesQueue?: Array<{ type?: string }>
  backoff?: { reset?: () => void }
  maxConnectTimeGenerator?: { reset?: () => void }
  tryReconnect?: () => void
}

/**
 * Rebuild the `start` queue on EVERY reconnect attempt, the library's own
 * included (PRO-888 review on #1260, R8).
 *
 * `tryReconnect()` re-queues a `start` for each active operation only while
 * `reconnecting` is false, and `reconnecting` stays true from the first retry
 * until an ack. So a retry socket that OPENS (flushing its `start`s onto the
 * wire) and is dropped BEFORE its ack — the backend's `on_connect` raising →
 * `connection_error` + close 4000 — leaves the next socket to be acked with
 * no subscription on it, while the server's 40 s keepalives keep the
 * client's 60 s liveness check green: a socket that is open, "connected",
 * and carries nothing, until a human clicks Reconnect. Dropping the queued
 * `start`s and re-queueing from the live `operations` on each attempt makes
 * every socket that reaches an ack carry every active operation, exactly
 * once (a `start` already queued for a dropped attempt is replaced, never
 * doubled). Other queued frames (a `stop` sent while CONNECTING) are kept.
 *
 * Same private reach-in as `reconnectWs` below, guarded the same way; the
 * instance property shadows the prototype method, which is what the client's
 * own `close()` calls. `ws-reconnect.test.ts` drives it over a fake socket.
 */
const WS_CLIENT_DRIFT_WARNING =
  '[ws] subscriptions-transport-ws has no tryReconnect(): the client is not ' +
  'the 0.9.19 this was written against — Reconnect falls back to a refetch ' +
  'and the start queue is not rebuilt. See ws-reconnect.test.ts.'

const installStartQueueRebuild = (): void => {
  const internals = wsClient as unknown as ReconnectInternals
  const original = internals.tryReconnect
  if (!original) {
    // eslint-disable-next-line no-console
    console.warn(WS_CLIENT_DRIFT_WARNING)
    return
  }
  internals.tryReconnect = () => {
    internals.unsentMessagesQueue = (
      internals.unsentMessagesQueue ?? []
    ).filter((frame) => frame?.type !== 'start')
    internals.reconnecting = false
    original.call(wsClient)
  }
}

installStartQueueRebuild()

export const reconnectWs = (): void => {
  const internals = wsClient as unknown as ReconnectInternals
  if (!internals.tryReconnect) {
    // eslint-disable-next-line no-console
    console.warn(WS_CLIENT_DRIFT_WARNING)
    return
  }
  internals.backoff?.reset?.()
  internals.maxConnectTimeGenerator?.reset?.()
  // The start queue itself is rebuilt by the patched `tryReconnect` above,
  // which `close(false, …)` ends in.
  if (internals.client) {
    // `close` emits `disconnected` synchronously: deliberate, so no toast.
    closingDeliberately = true
    try {
      wsClient.close(false, false)
    } finally {
      closingDeliberately = false
    }
    return
  }
  internals.tryReconnect()
}
